import React, { useEffect, useRef, useState } from 'react'
import Chart from 'chart.js/auto'
import { productionService } from '../../../services/apiRemoteProduction'
import './Statistics.css'

// Helper for pie chart colors
const getDefaultColors = (numColors) => {
  const colors = [
    '#ffa400', '#00c9a7', '#ff6384', '#36a2eb', '#cc65fe', 
    '#ff9f40', '#4bc0c0', '#9966ff', '#ffcd56', '#c9cbcf'
  ];
  // Extend with random colors if more are needed
  while (colors.length < numColors) {
    colors.push(`rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.7)`);
  }
  return colors.slice(0, numColors);
};

function Statistics() {
  const chartsRef = useRef([])
  const tonnageChartRef = useRef(null)
  const gradeChartRef = useRef(null)
  const goldRecoveryRef = useRef(null)
  const sourceTonnagePieRef = useRef(null)
  const sourceMetalContentPieRef = useRef(null)
  const millingPerformanceRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [chartData, setChartData] = useState({
    oreData: null,
    processingData: null,
    millingData: null
  })

  const initialStartDate = new Date();
  initialStartDate.setFullYear(initialStartDate.getFullYear() - 1);
  const [dateRange, setDateRange] = useState({
    startDate: initialStartDate,
    endDate: new Date()
  })
  const [timeGrouping, setTimeGrouping] = useState('month')
  
  const handleQuickDateRange = (rangeType) => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date(today); // Ensure endDate is a new object

    switch (rangeType) {
      case 'today':
        startDate = new Date(today.setHours(0, 0, 0, 0));
        endDate = new Date(today.setHours(23, 59, 59, 999));
        break;
      case 'thisWeek':
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Adjust for Sunday start
        firstDayOfWeek.setHours(0, 0, 0, 0);
        startDate = firstDayOfWeek;
        // endDate is today
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        // endDate is today
        break;
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1);
        // endDate is today
        break;
      default:
        return;
    }
    setDateRange({ startDate, endDate });
  };

  const formatDateByGrouping = (date) => {
    const d = new Date(date)
    switch(timeGrouping) {
      case 'day':
        return d.toISOString().split('T')[0]
      case 'week':
        const weekNum = Math.ceil((d.getDate() + d.getDay()) / 7)
        return `Week ${weekNum}, ${d.getFullYear()}`
      case 'month':
        return d.toLocaleDateString('default', { month: 'short', year: 'numeric' })
      case 'quarter':
        const quarter = Math.floor(d.getMonth() / 3) + 1
        return `Q${quarter} ${d.getFullYear()}`
      case 'year':
        return d.getFullYear().toString()
      default:
        return d.toLocaleDateString()
    }
  }

  const destroyCharts = () => {
    chartsRef.current.forEach(chart => {
      if (chart) {
        chart.destroy()
      }
    })
    chartsRef.current = []
  }

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        const params = {
          groupBy: timeGrouping,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0]
        };

        const [oreData, processingData, millingData] = await Promise.all([
          productionService.getOreProduction(params),
          productionService.getProcessing(params),
          productionService.getMilling(params)
        ]);

        if (!isMounted) return;

        setChartData({
          oreData,
          processingData,
          millingData
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      destroyCharts();
    };
  }, [timeGrouping, dateRange]);

  useEffect(() => {
    if (loading || error || !chartData.oreData || !chartData.processingData || !chartData.millingData) {
      return;
    }

    if (!tonnageChartRef.current || !gradeChartRef.current || !goldRecoveryRef.current || 
        !sourceTonnagePieRef.current || !sourceMetalContentPieRef.current || !millingPerformanceRef.current) {
      return;
    }

    destroyCharts();
    
    const processedOreData = chartData.oreData.monthlyProduction;
    const processedProcessingData = chartData.processingData.monthlyProcessing;
    const processedMillingData = chartData.millingData.monthlyMilling;

    if (tonnageChartRef.current && processedOreData) {
      const ctx = tonnageChartRef.current.getContext('2d');
      const tonnageChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: processedOreData.map(item => productionService.formatPeriod(item.period, timeGrouping)),
          datasets: [{
            label: 'Total Tonnage',
            data: processedOreData.map(item => item.sources.reduce((sum, s) => sum + s.total_tonnage, 0)),
            backgroundColor: '#ffa400',
            borderColor: '#ffa400',
            borderWidth: 1,
            borderRadius: 6,
            maxBarThickness: 32
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Ore Production - Tonnage',
              color: '#fff',
              font: {
                family: 'Inter',
                size: 16
              }
            },
            legend: {
              labels: {
                color: '#fff',
                font: {
                  family: 'General Sans',
                  size: 12
                }
              }
            }
          },
          scales: {
            x: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#fff'
              }
            },
            y: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#fff',
                callback: function(value) {
                  return productionService.formatNumber(value)
                }
              }
            }
          }
        }
      });
      chartsRef.current.push(tonnageChart);
    }

    if (gradeChartRef.current && processedOreData) {
      const ctx = gradeChartRef.current.getContext('2d')
      const gradeChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: processedOreData.map(item => productionService.formatPeriod(item.period, timeGrouping)),
          datasets: [{
            label: 'Average Grade (g/t)',
            data: processedOreData.map(item => {
              const totalTonnage = item.sources.reduce((sum, s) => sum + s.total_tonnage, 0);
              const weightedGradeSum = item.sources.reduce((sum, s) => sum + (s.avg_grade * s.total_tonnage), 0);
              return totalTonnage > 0 ? weightedGradeSum / totalTonnage : 0;
            }),
            borderColor: '#00c9a7',
            backgroundColor: 'rgba(0, 201, 167, 0.2)',
            borderWidth: 2,
            tension: 0.4,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Ore Production - Grade',
              color: '#fff',
              font: {
                family: 'Inter',
                size: 16
              }
            },
            legend: {
              labels: {
                color: '#fff',
                font: {
                  family: 'General Sans',
                  size: 12
                }
              }
            }
          },
          scales: {
            x: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#fff'
              }
            },
            y: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#fff',
                callback: function(value) {
                  return productionService.formatNumber(value) + ' g/t'
                }
              }
            }
          }
        }
      })
      chartsRef.current.push(gradeChart)
    }

    if (goldRecoveryRef.current && processedProcessingData) {
      const ctx = goldRecoveryRef.current.getContext('2d')
      const goldRecoveryChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: processedProcessingData.map(item => productionService.formatPeriod(item.period, timeGrouping)),
          datasets: [
            {
              label: 'Recovery Rate (%)',
              data: processedProcessingData.map(item => item.recovery),
              borderColor: '#ffa400',
              backgroundColor: 'rgba(255, 164, 0, 0.2)',
              yAxisID: 'y',
              tension: 0.4,
              pointBackgroundColor: '#ffa400',
              pointBorderColor: '#fff',
              pointRadius: 5,
              fill: false
            },
            {
              label: 'Feed Grade (g/t)',
              data: processedProcessingData.map(item => item.feed.grade),
              borderColor: '#00c9a7',
              backgroundColor: 'rgba(0, 201, 167, 0.2)',
              yAxisID: 'y1',
              tension: 0.4,
              pointBackgroundColor: '#00c9a7',
              pointBorderColor: '#fff',
              pointRadius: 5,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: '#fff',
                font: {
                  family: 'General Sans',
                  size: 12
                },
                padding: 15
              }
            },
            title: {
              display: true,
              text: 'Processing Performance',
              color: '#fff',
              font: {
                family: 'Inter',
                size: 16
              },
              padding: {
                bottom: 15
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  if (context.dataset.label === 'Recovery Rate (%)') {
                    return `${context.dataset.label}: ${productionService.formatNumber(context.parsed.y)}%`
                  } else {
                    return `${context.dataset.label}: ${productionService.formatNumber(context.parsed.y)} g/t`
                  }
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Recovery Rate (%)',
                color: '#ffa400',
                font: {
                  family: 'Inter'
                }
              },
              min: 0,
              max: 100,
              ticks: {
                color: '#ffa400',
                font: {
                  family: 'General Sans'
                },
                callback: function(value) {
                  return productionService.formatNumber(value) + '%'
                }
              },
              grid: {
                color: 'rgba(255, 164, 0, 0.1)'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Feed Grade (g/t)',
                color: '#00c9a7',
                font: {
                  family: 'Inter'
                }
              },
              ticks: {
                color: '#00c9a7',
                font: {
                  family: 'General Sans'
                },
                callback: function(value) {
                  return productionService.formatNumber(value)
                }
              },
              grid: {
                drawOnChartArea: false
              }
            },
            x: {
              ticks: {
                color: '#fff',
                font: {
                  family: 'General Sans'
                }
              },
              grid: {
                color: 'rgba(255,255,255,0.1)'
              }
            }
          }
        }
      })
      chartsRef.current.push(goldRecoveryChart)
    }

    const latestPeriodOreData = processedOreData && processedOreData.length > 0 ? processedOreData[processedOreData.length - 1] : null;

    if (latestPeriodOreData && latestPeriodOreData.sources && latestPeriodOreData.sources.length > 0) {
      const sourceNames = latestPeriodOreData.sources.map(s => s.source_name);
      const sourceTonnages = latestPeriodOreData.sources.map(s => s.total_tonnage);
      const sourceMetalContent = latestPeriodOreData.sources.map(s => s.total_tonnage * s.avg_grade);
      const pieColors = getDefaultColors(sourceNames.length);

      if (sourceTonnagePieRef.current) {
        const ctx = sourceTonnagePieRef.current.getContext('2d');
        const tonnagePieChart = new Chart(ctx, {
          type: 'pie',
          data: {
            labels: sourceNames,
            datasets: [{
              label: 'Tonnage by Source',
              data: sourceTonnages,
              backgroundColor: pieColors,
              borderColor: '#fff',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  color: '#fff'
                }
              },
              title: {
                display: true,
                text: `Tonnage by Source (${productionService.formatPeriod(latestPeriodOreData.period, timeGrouping)})`,
                color: '#fff'
              }
            }
          }
        });
        chartsRef.current.push(tonnagePieChart);
      }

      if (sourceMetalContentPieRef.current) {
        const ctx = sourceMetalContentPieRef.current.getContext('2d');
        const metalPieChart = new Chart(ctx, {
          type: 'pie',
          data: {
            labels: sourceNames,
            datasets: [{
              label: 'Metal Content by Source',
              data: sourceMetalContent,
              backgroundColor: pieColors,
              borderColor: '#fff',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  color: '#fff'
                }
              },
              title: {
                display: true,
                text: `Metal Content by Source (${productionService.formatPeriod(latestPeriodOreData.period, timeGrouping)})`,
                color: '#fff'
              }
            }
          }
        });
        chartsRef.current.push(metalPieChart);
      }
    }

    if (millingPerformanceRef.current && processedMillingData) {
      const ctx = millingPerformanceRef.current.getContext('2d')
      const millingChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: processedMillingData.map(item => productionService.formatPeriod(item.period, timeGrouping)),
          datasets: [
            {
              label: 'Total Tonnage Milled',
              data: processedMillingData.map(item => item.mills.reduce((sum, m) => sum + m.total_tonnage, 0)),
              borderColor: '#ffa400',
              backgroundColor: 'rgba(255, 164, 0, 0.2)',
              fill: true,
              tension: 0.4,
              pointBackgroundColor: '#ffa400',
              pointBorderColor: '#fff',
              pointRadius: 5,
            },
            {
              label: 'Average Mill Runtime (hrs)',
              data: processedMillingData.map(item => {
                const totalRuntime = item.mills.reduce((sum, m) => sum + m.avg_runtime, 0);
                return item.mills.length > 0 ? totalRuntime / item.mills.length : 0;
              }),
              borderColor: '#00c9a7',
              backgroundColor: 'rgba(0, 201, 167, 0.2)',
              fill: true,
              tension: 0.4,
              pointBackgroundColor: '#00c9a7',
              pointBorderColor: '#fff',
              pointRadius: 5,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Milling Performance',
              color: '#fff',
              font: {
                family: 'Inter',
                size: 16
              }
            },
            legend: {
              position: 'top',
              labels: {
                color: '#fff',
                font: {
                  family: 'General Sans',
                  size: 12
                }
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed.y !== null) {
                    label += productionService.formatNumber(context.parsed.y);
                    if(context.dataset.label.toLowerCase().includes('runtime')) label += ' hrs';
                  }
                  return label;
                }
              }
            }
          },
          interaction: {
            mode: 'index',
            intersect: false
          },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Value',
                color: '#fff',
                font: {
                  family: 'Inter'
                }
              },
              ticks: {
                color: '#fff',
                font: {
                  family: 'General Sans'
                },
                callback: function(value) {
                  return productionService.formatNumber(value)
                }
              },
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              }
            },
            x: {
              ticks: {
                color: '#fff',
                font: {
                  family: 'General Sans'
                }
              },
              grid: {
                color: 'rgba(255,255,255,0.1)'
              }
            }
          }
        }
      });
      chartsRef.current.push(millingChart)
    }
  }, [chartData, timeGrouping, loading, error]);

  if (loading) {
    return <div className="statisticsWrapper">Loading...</div>
  }

  if (error) {
    return <div className="statisticsWrapper">Error: {error}</div>
  }

  return (
    <div className="statisticsWrapper">
      <div className="filterControls">
        <div className="dateControls">
          <div className="dateInput">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate.toISOString().split('T')[0]}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                startDate: new Date(e.target.value)
              }))}
            />
          </div>
          <div className="dateInput">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate.toISOString().split('T')[0]}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                endDate: new Date(e.target.value)
              }))}
            />
          </div>
        </div>
        <div className="quickDateRanges">
          <button onClick={() => handleQuickDateRange('today')}>Today</button>
          <button onClick={() => handleQuickDateRange('thisWeek')}>This Week</button>
          <button onClick={() => handleQuickDateRange('thisMonth')}>This Month</button>
          <button onClick={() => handleQuickDateRange('thisYear')}>This Year</button>
        </div>
        <div className="groupingControl">
          <label>Group By:</label>
          <select
            value={timeGrouping}
            onChange={(e) => setTimeGrouping(e.target.value)}
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </select>
        </div>
      </div>

      <div className="graphsGrid">
        <div className="graphCard">
          <div className="contentGraph">
            <h1>Ore Production - Tonnage</h1>
            <p>Total ore tonnage over time</p>
          </div>
          <div className="canvasWrapper" style={{ height: '300px', width: '100%' }}>
            <canvas ref={tonnageChartRef}></canvas>
          </div>
        </div>

        <div className="graphCard">
          <div className="contentGraph">
            <h1>Ore Production - Grade</h1>
            <p>Average ore grade over time</p>
          </div>
          <div className="canvasWrapper" style={{ height: '300px', width: '100%' }}>
            <canvas ref={gradeChartRef}></canvas>
          </div>
        </div>

        <div className="graphCard">
          <div className="contentGraph">
            <h1>Processing Performance</h1>
            <p>Recovery rates and feed grades</p>
          </div>
          <div className="canvasWrapper" style={{ height: '300px', width: '100%' }}>
            <canvas ref={goldRecoveryRef}></canvas>
          </div>
        </div>

        <div className="graphCard">
          <div className="contentGraph">
            <h1>Production by Source - Tonnage</h1>
            <p>Tonnage distribution by source (latest period)</p>
          </div>
          <div className="canvasWrapper" style={{ height: '300px', width: '100%' }}>
            <canvas ref={sourceTonnagePieRef}></canvas>
          </div>
        </div>
        
        <div className="graphCard">
          <div className="contentGraph">
            <h1>Production by Source - Metal Content</h1>
            <p>Metal content distribution by source (latest period)</p>
          </div>
          <div className="canvasWrapper" style={{ height: '300px', width: '100%' }}>
            <canvas ref={sourceMetalContentPieRef}></canvas>
          </div>
        </div>

        <div className="graphCard">
          <div className="contentGraph">
            <h1>Milling Performance</h1>
            <p>Milling throughput and runtime</p>
          </div>
          <div className="canvasWrapper" style={{ height: '300px', width: '100%' }}>
            <canvas ref={millingPerformanceRef}></canvas>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Statistics 